import React, { useState } from "react";
import { mexicanStates, usStates } from "../../Helper/CountryStates";
import { useTranslation } from "react-i18next";
import MaintenanceReportLite from "../MaintenanceReports/Modals/MaintenanceReportLite";
import TruckOutSearchSelect from "../../Components/Inputs/TruckOutSearch";
import TrailerOutSearchSelect from "../../Components/Inputs/TrailerOutSearch";
import DriverOutSearch from "../../Components/Inputs/DriverOutSearch";
import { FormInput, FormSelect } from "../../Components/Inputs/FormInputs";
import { COUNTRY_OPTIONS } from "../../Constants/CheckInOut";
import { GetVehicleCondition } from "../../Components/Tables/Vehicles/GetVehicleCondition";

const CheckOutForm = ({
  formState,
  formData,
  setFormData,
  dispatchForm,
  close,
  nextForm,
}) => {
  const { t } = useTranslation();
  const [filteredCustomers, setFilteredCustomers] = useState([]);

  const [truckSearch, setTruckSearch] = useState("");

  const [maintenanceReport, setMaintenanceReport] = useState(null);

  const handleFormField = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpperCase = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value.toUpperCase(),
    }));
  };

  // Updates only states for the selected truck in Select component
  const handleTruckSelect = (selectedTruck) => {
    if (!selectedTruck) {
      setFormData((prev) => ({
        ...prev,
        truckId: null,
        truckPlate: "",
        truckOption: null,
        truckCondition: "",
        truckConditionNotes: "",
        odometer: "",
      }));
      dispatchForm({
        type: "ERROR_MESSAGE",
        key: null,
        value: null,
      });
      return;
    }

    const hasRepairsOpen = selectedTruck.truckRepairs?.length > 0;
    setFormData((prev) => ({
      ...prev,
      truckId: selectedTruck.id,
      truckCondition: selectedTruck.truck_condition,
      truckPlate: selectedTruck.truck_plate,
      truckConditionNotes: selectedTruck.truck_condition_notes,
      odometer: selectedTruck.odometer,
      truckOption: {
        value: selectedTruck.value,
        label: selectedTruck.label,
      },
    }));
    if (hasRepairsOpen)
      dispatchForm({
        type: "ERROR_MESSAGE",
        key: "truckErrorMessage",
        value: null,
      });
  };

  const handleCustomerClick = (name) => {
    setFilteredCustomers(null);
    setFormData((prev) => ({
      ...prev,
      customer: name,
    }));
  };

  const handleTrailerSelect = (option) => {
    console.log(option);
    if (!option) {
      setFormData((prev) => ({
        ...prev,
        trailerId: null,
        trailerPlate: "",
        trailerDimensions: null,
        isLoaded: null,
        customer: "",
        sealNumber: "",
        trailerCondition: "",
        trailerConditionNotes: "",
        internalSeal: null,
        trailerOption: null,
      }));
      dispatchForm({
        type: "LOCK_TRUCK_INPUT",
        message: null,
        value: false,
      });
      return;
    }

    const reservation = option.reservation; // Handle auto locking truck if trailer is reserved
    let message = "";
    let reserved_by = "";
    if (reservation) {
      reserved_by = `${t("forms.type.vehicle.check-out.reserved-by")} ${
        // Define user reservation
        reservation.user.first_name
      } ${reservation.user.last_name}.`;

      const truck_asset = reservation.truck_asset;
      if (truck_asset) {
        setTruckSearch(truck_asset.unit);

        message = `${t("forms.type.vehicle.check-out.trailer-is-reserved")} ${
          truck_asset.unit
        }. ${reserved_by}`;

        dispatchForm({
          type: "LOCK_TRUCK_INPUT",
          message: message,
          value: true,
        });
      } else {
        message = reserved_by;
        dispatchForm({
          type: "LOCK_TRUCK_INPUT",
          message: message,
          value: false,
        });
      }
    } else {
      dispatchForm({
        type: "LOCK_TRUCK_INPUT",
        message: message,
        value: false,
      });
    }

    setFormData((prev) => ({
      ...prev,
      trailerId: option.id,
      trailerPlate: option.license_plate,
      trailerDimensions: {
        width: option.trailer_dimensions.width,
        height: option.trailer_dimensions.height,
        length: option.trailer_dimensions.length,
      },
      isLoaded: option.is_loaded,
      customer: option.customer,
      sealNumber: option.seal_number,
      trailerCondition: option.trailer_condition,
      trailerConditionNotes: option.trailer_condition_notes,
      internalSeal: option.internal_seal,
      trailerOption: {
        label: option.label,
        value: option.value,
      },
    })); // Update trailer number in the final step
  };

  const storeData = () => {
    localStorage.setItem("checkOutForm", JSON.stringify(formState));
    localStorage.setItem("checkOutData", JSON.stringify(formData));
  };

  const clearData = () => {
    localStorage.removeItem("checkOutForm");
    localStorage.removeItem("checkOutData");
  };

  return (
    <>
      {maintenanceReport && (
        <MaintenanceReportLite
          report={maintenanceReport}
          close={() => setMaintenanceReport(null)}
        />
      )}
      <div className="white-cont">
        <h2 className="global-h2 light-black">
          {t("forms.type.vehicle.check-out.header")}
        </h2>

        <form
          onSubmit={(e) => {
            nextForm(e);
            storeData();
          }}
        >
          {/* Form group: Is truck hauling trailer */}
          <div className="form-group">
            <label className="input-label required">
              {t("forms.type.vehicle.input-headers.carrying-bool")}{" "}
            </label>
            <div className="input-cont">
              <fieldset className="fieldsets">
                <div className="radio-options">
                  <input
                    type="radio"
                    data-testid="carrying-yes"
                    name="group1"
                    className="radio-input"
                    checked={formState.isCarrying}
                    onChange={() =>
                      dispatchForm({
                        type: "IS_CARRYING",
                        payload: true,
                      })
                    }
                    required
                  />
                  <label>{t("general.yes")}</label>
                </div>
                <div className="radio-options">
                  <input
                    type="radio"
                    name="group1"
                    data-testid="carrying-no"
                    className="radio-input"
                    checked={formState.isCarrying === false}
                    onChange={() => {
                      dispatchForm({ type: "IS_CARRYING", payload: false });
                      handleTrailerSelect(null);
                    }}
                  />
                  <label>No</label>
                </div>
              </fieldset>
            </div>
          </div>

          {/* Form group: Truck number */}
          <div className="form-group">
            <label className="input-label required">
              {t("forms.type.vehicle.input-headers.truck-number")}{" "}
            </label>
            <div className="input-cont-block input-cont-message">
              <div className="input-cont input-cont-margin-bottom">
                <TruckOutSearchSelect
                  onSelect={handleTruckSelect}
                  selectedTruck={formData.truckOption}
                />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="input-label">
              {t("forms.type.vehicle.input-headers.truck-plate")}
            </label>
            <div className="input-cont">
              <span className="input-label">{formData.truckPlate}</span>
            </div>
          </div>
          {/* Form group: Odometer */}
          <FormInput
            label={"odometer"}
            value={formData.odometer}
            type="number"
            onChange={handleFormField}
          />

          <div className="form-group">
            <label className="input-label">
              {t("forms.type.vehicle.labels.truckCondition")}
            </label>
            <div className="input-cont">
              <span className="input-label">
                {formData.truckCondition && (
                  <GetVehicleCondition condition={formData.truckCondition} />
                )}
              </span>
            </div>
          </div>

          <div className="form-group">
            <label className="input-label"></label>
            <div className="input-cont">
              <span className="input-label">
                {formData.truckConditionNotes}
              </span>
            </div>
          </div>

          {/* Form group: Driver */}
          <div className="form-group">
            <label className="input-label required">
              {t("yard-tables.search-filters.dropdowns.driver")}{" "}
            </label>
            <div className="input-cont">
              <DriverOutSearch
                onSelect={(option) => {
                  if (option) {
                    setFormData((prev) => ({
                      ...prev,
                      visitorId: option.id,
                      visitorOption: {
                        value: option.value,
                        label: option.label,
                      },
                    }));
                  } else {
                    setFormData((prev) => ({
                      ...prev,
                      visitorId: null,
                      visitorOption: null,
                    }));
                  }
                }}
                selectedDriver={formData.visitorOption}
              />
            </div>
          </div>

          {/* All trailer inputs */}
          {formState.isCarrying && (
            <>
              {/* // Form group: Trailer number */}
              <div className="form-group">
                <label className="input-label required">
                  {t("forms.type.vehicle.input-headers.trailer-number")}{" "}
                </label>
                <div className="input-cont-block input-cont-message">
                  <div className="input-cont input-cont-margin-bottom">
                    <TrailerOutSearchSelect
                      onSelect={(option) => handleTrailerSelect(option)}
                      selectedTrailer={formData.trailerOption}
                    />
                  </div>
                  {formState.trailerErrorMessage && (
                    <p className="error">{formState.trailerErrorMessage}</p>
                  )}
                </div>
              </div>

              {/* Trailer license plate */}
              <div className="form-group">
                <label className="input-label">
                  {t("forms.type.vehicle.labels.trailerPlate")}
                </label>
                <div className="input-cont">
                  <span>{formData.trailerPlate}</span>
                </div>
              </div>

              {/* Form group: Trailer dimensions */}
              <div className="form-group">
                <label className="input-label">
                  {t("forms.type.vehicle.input-headers.trailer-dim")}
                </label>
                <div className="input-cont">
                  <span className="">
                    {t("forms.type.vehicle.placeholders.length")}:{" "}
                    {formData.trailerDimensions?.length || 0}
                  </span>
                  <span className="">
                    {t("forms.type.vehicle.placeholders.width")}:{" "}
                    {formData.trailerDimensions?.width || 0}
                  </span>
                  <span className="">
                    {t("forms.type.vehicle.placeholders.height")}:{" "}
                    {formData.trailerDimensions?.height || 0}
                  </span>
                </div>
              </div>

              <div className="form-group">
                <label className="input-label">
                  {t("forms.type.vehicle.labels.isLoaded")}
                </label>
                <div className="input-cont">
                  <span className="">
                    {formData.isLoaded
                      ? t("forms.type.vehicle.select.isLoaded.loaded")
                      : t("forms.type.vehicle.select.isLoaded.empty")}
                  </span>
                </div>
              </div>

              {/* // Form group: Customer */}
              <div className="form-group">
                <label
                  className={`input-label ${
                    formData.isLoaded ? "required" : ""
                  }`}
                >
                  {t("yard-tables.search-filters.dropdowns.customer")}
                </label>
                <div className="input-cont">
                  <div className="search-input">
                    <input
                      id="searchCustomer"
                      type="text"
                      className="text-input"
                      placeholder={t(
                        "forms.type.vehicle.placeholders.search-customer",
                      )}
                      value={formData.customer}
                      onChange={(e) => setFormData((prev) => ({
                        ...prev,
                        customer: e.target.value.toUpperCase(),
                      }))}
                      required={formState.isLoaded}
                    />
                    {filteredCustomers?.length > 0 && (
                      <ul
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          width: "100%",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          maxHeight: "150px",
                          overflowY: "auto",
                          backgroundColor: "white",
                          listStyleType: "none",
                          padding: 0,
                          margin: 0,
                          zIndex: 1000,
                        }}
                      >
                        {filteredCustomers.map((customer, index) => (
                          <li
                            key={index}
                            onClick={() => handleCustomerClick(customer.name)}
                            style={{
                              padding: "8px",
                              cursor: "pointer",
                              borderBottom: "1px solid #ccc",
                            }}
                          >
                            {customer.name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>

              {/* Form group: Seal */}
              <FormInput
                label={"sealNumber"}
                value={formData.sealNumber}
                onChange={(e) => setFormData((prev) => ({
                  ...prev,
                  sealNumber: e.target.value.toUpperCase().split(" ").join(""),
                }))}
                required={false}
              />

              {/* Form group: Internal seal */}
              <div className="form-group">
                <label className="input-label">
                  {t("yard-tables.table-columns.internalSeal")}
                </label>
                <div className="input-cont">
                  <span className="input-label">
                    {formData.internalSeal
                      ? formData.internalSeal.seal_number
                      : t("forms.type.vehicle.placeholders.none")}
                  </span>
                </div>
              </div>

              {/* Form group: Trailer condition */}
              <div className="form-group">
                <label className="input-label">
                  {t("forms.type.vehicle.labels.trailerCondition")}
                </label>
                <div className="input-cont">
                  <span className="input-label">
                    {formData.trailerCondition && (
                      <GetVehicleCondition
                        condition={formData.trailerCondition.toLowerCase()}
                      />
                    )}
                  </span>
                </div>
              </div>
              <div className="form-group">
                <label className="input-label"></label>
                <div className="input-cont">
                  <span className="input-label">
                    {formData.trailerConditionNotes}
                  </span>
                </div>
              </div>
            </>
          )}

          <div className="form-group">
            <label className="input-label">
              {t("yard-tables.search-filters.dropdowns.destination")}
            </label>
          </div>
          {/* Form group: Destination */}
          <FormSelect
            options={COUNTRY_OPTIONS}
            label={"country"}
            value={formData.country}
            onChange={handleFormField}
          />

          {/* Form group: State */}
          <div className="form-group">
            <label className="input-label required">
              {t("forms.type.vehicle.labels.state")}
            </label>
            <div className="input-cont">
              <select
                id="state"
                name="state"
                data-testid="state"
                value={formData.state}
                onChange={handleFormField}
                required
              >
                <option value="" selected disabled>
                  {t("forms.type.vehicle.placeholders.destination.state")}
                </option>
                {formData.country === "USA"
                  ? usStates.map((state, index) => (
                      <option key={index} value={state}>
                        {state}
                      </option>
                    ))
                  : mexicanStates.map((state, index) => (
                      <option key={index} value={state}>
                        {state}
                      </option>
                    ))}
              </select>
            </div>
          </div>

          {/* Form group: City */}
          <FormInput
            label={"city"}
            value={formData.city}
            onChange={handleUpperCase}
          />

          {/* Form group: Notes */}
          <FormInput
            label={"notes"}
            value={formData.notes}
            onChange={handleFormField}
            required={false}
          />

          {/* Buttons cont */}
          <div className="full-cont">
            <button
              className="change-pwd-btn"
              id="continue-btn"
              data-testid="continue-btn"
              type="submit"
            >
              {t("buttons.continue")}
            </button>
            <button
              className="cancel-btn"
              onClick={() => {
                close();
                clearData();
              }}
            >
              {t("buttons.cancel")}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default React.memo(CheckOutForm);
